import React from 'react';
import { createPortal } from 'react-dom';
import { Activity, Copy, Plus } from '../../bravosIcons';
import type { WorkoutSummary } from '../../../../types/dataModel';

export interface PlanSelectionBarProps {
  visible: boolean;
  selectedCount: number;
  onAddWorkout: () => void;
  onMarkRest: () => void;
  onClear: () => void;
}

export function PlanSelectionBar({
  visible,
  selectedCount,
  onAddWorkout,
  onMarkRest,
  onClear,
}: PlanSelectionBarProps) {
  if (!visible || selectedCount === 0) return null;

  return createPortal(
    <div className="twp-selbar twp-selbar--visible">
      <span className="twp-selbar-count">
        <strong>{selectedCount}</strong> day{selectedCount === 1 ? '' : 's'} selected
      </span>
      <span className="twp-vsep" aria-hidden="true" />
      <button type="button" className="twp-selbar-btn twp-selbar-btn--primary" onClick={onAddWorkout}>
        <Plus size={15} strokeWidth={2.2} />
        Add workout
      </button>
      <button type="button" className="twp-selbar-btn" onClick={onMarkRest}>
        <Activity size={15} strokeWidth={2} />
        Mark as rest
      </button>
      <button type="button" className="twp-selbar-btn twp-selbar-btn--clear" onClick={onClear}>
        Clear
      </button>
    </div>,
    document.body,
  );
}

export interface PlanClipBarProps {
  workout: WorkoutSummary;
  pasteCount: number;
  onDone: () => void;
}

export function PlanClipBar({ workout, pasteCount, onDone }: PlanClipBarProps) {
  return createPortal(
    <div className="twp-clipbar twp-clipbar--visible">
      <span className="twp-clip-ic" aria-hidden="true">
        <Copy size={15} strokeWidth={2} />
      </span>
      <span className="twp-clip-txt">
        Copying <strong>{workout.name}</strong>
      </span>
      <span className="twp-vsep" aria-hidden="true" />
      <span className="twp-clip-hint">Click any day to paste · Esc to finish</span>
      {pasteCount > 0 ? <span className="twp-clip-count">Pasted {pasteCount}</span> : null}
      <span className="twp-vsep" aria-hidden="true" />
      <button type="button" className="twp-clipbar-btn" onClick={onDone}>
        Done
      </button>
    </div>,
    document.body,
  );
}

export interface PlanEditBarProps {
  visible: boolean;
  isSaving: boolean;
  onDiscard: () => void;
  onSave: () => void;
}

export function PlanEditBar({ visible, isSaving, onDiscard, onSave }: PlanEditBarProps) {
  if (!visible) return null;

  return createPortal(
    <div className="twp-save-bar-dock">
      <div className="twp-save-bar" role="region" aria-label="Unsaved plan changes">
        <div className="twp-save-bar-status">There are unsaved changes.</div>
        <div className="twp-save-bar-actions">
          <button type="button" className="twp-save-bar-btn twp-save-bar-btn--ghost" onClick={onDiscard}>
            Discard
          </button>
          <button
            type="button"
            className="twp-save-bar-btn twp-save-bar-btn--save"
            disabled={isSaving}
            onClick={onSave}
          >
            {isSaving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
